import React, { useEffect, useState } from "react";
import { Box, Typography, Avatar, Container, Grid, Paper } from "@mui/material";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export default function HeroSection() {
  // Typing effect for title
  const titles = React.useMemo(() => [
    "Python, Java, C#, R, JavaScript",
    "AWS & Azure Solutions",
    "Docker & Kubernetes Orchestration",
    "Cloud Automation",
    "Serverless Architectures",
    "Terraform & Jenkins",
    "Spring Boot & Flask",
    "Data Engineering",
    "ETL Pipeline Optimization",
    "Facial Recognition (AWS Rekognition)",
    "Neural Networks (TensorFlow, PyTorch)",
    "API Development",
    "Cloud Migration",
    "Serverless Security",
    "Data Analytics Engineering"
  ], []);
  const [displayedTitle, setDisplayedTitle] = useState("");
  const [titleIndex, setTitleIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let timeout;
    const currentTitle = (titles && titles.length > 0 && titles[titleIndex]) ? titles[titleIndex] : "";
    if (!deleting && charIndex < currentTitle.length) {
      timeout = setTimeout(() => {
        setDisplayedTitle(currentTitle.slice(0, charIndex + 1));
        setCharIndex(charIndex + 1);
      }, 80);
    } else if (deleting && charIndex > 0) {
      timeout = setTimeout(() => {
        setDisplayedTitle(currentTitle.slice(0, charIndex - 1));
        setCharIndex(charIndex - 1);
      }, 40);
    } else if (!deleting && charIndex === currentTitle.length && currentTitle.length > 0) {
      timeout = setTimeout(() => setDeleting(true), 1200);
    } else if (deleting && charIndex === 0 && titles && titles.length > 0) {
      timeout = setTimeout(() => {
        setDeleting(false);
        setTitleIndex((titleIndex + 1) % titles.length);
      }, 400);
    }
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, titleIndex, titles]);

  const glowStyle = {
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderColor: 'var(--pacific-cyan)'
    }
  };

  return (
    <Box id="hero" sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      pt: 8,
      pb: 6
    }}>
      <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

        {/* Headshot */}
        <Avatar
          src={require('./headshot.jpeg')}
          alt="Sid Kumaran"
          sx={{
            width: 180,
            height: 180,
            border: '3px solid var(--pacific-cyan)',
            mb: 5
          }}
        />

        {/* Name & Title */}
        <Typography variant="h2" component="h1" sx={{
          fontWeight: 700,
          mb: 2,
          textAlign: 'center',
          color: 'var(--rosewood)',
          letterSpacing: '-0.5px'
        }}>
          Sid Kumaran
        </Typography>

        <Typography variant="h5" sx={{
          color: 'var(--chinese-violet)',
          mb: 4,
          textAlign: 'center',
          fontWeight: 500,
          maxWidth: '600px',
          lineHeight: 1.6
        }}>
          Full Stack Engineer & Cloud Architect
        </Typography>

        {/* Typing Effect */}
        <Box sx={{
          height: '3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 6
        }}>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: 'var(--pacific-cyan)',
            textAlign: 'center',
            fontFamily: 'monospace'
          }}>
            {displayedTitle}
            <span style={{ animation: 'blink 1s infinite', color: 'var(--rose-red)' }}>|</span>
          </Typography>
        </Box>

        {/* Social Links */}
        <Grid container spacing={3} justifyContent="center">
          <Grid>
            <a href="https://www.linkedin.com/in/sid-kumaran/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <Paper elevation={0} sx={{
                p: 1.5,
                borderRadius: '50%',
                background: 'var(--white)',
                border: '1px solid rgba(0,0,0,0.15)',
                ...glowStyle
              }}>
                <LinkedInIcon sx={{ fontSize: 32, color: 'var(--pacific-cyan)' }} />
              </Paper>
            </a>
          </Grid>
          <Grid>
            <a href="https://github.com/siddharthkumaran" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <Paper elevation={0} sx={{
                p: 1.5,
                borderRadius: '50%',
                background: 'var(--white)',
                border: '1px solid rgba(0,0,0,0.15)',
                ...glowStyle
              }}>
                <GitHubIcon sx={{ fontSize: 32, color: 'var(--black)' }} />
              </Paper>
            </a>
          </Grid>
          <Grid>
            <a
                aria-label="Send email"
				href="mailto:sidd.kumaran@gmail.com"
				onClick={(e) => {
					e.preventDefault();
					window.location.href = "mailto:sidd.kumaran@gmail.com";
				}}
			>
              <Paper elevation={0} sx={{
                p: 1.5,
                borderRadius: '50%',
                background: 'var(--white)',
                border: '1px solid rgba(0,0,0,0.15)',
                ...glowStyle
              }}>
                <EmailIcon sx={{ fontSize: 32, color: 'var(--cardinal)' }} />
              </Paper>
            </a>
          </Grid>
        </Grid>

        {/* Scroll Down Arrow */}
        <Box sx={{ mt: 4, cursor: 'pointer' }} 
          onClick={() => {
            const nextSection = document.getElementById('skills');
            if (nextSection) {
              nextSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 40, color: 'var(--chinese-violet)', opacity: 0.6 }} />
        </Box>
      </Container>

      <style>{`
        @keyframes blink { 50% { opacity: 0; } }
      `}</style>
    </Box>
  );
}
